import React, { useState, useEffect, useCallback } from 'react';
import {
  Modal,
  Form,
  Select,
  Input,
  InputNumber,
  Button,
  Steps,
  Space,
  Card,
  Checkbox,
  message,
  Typography,
  Row,
  Col,
  Spin,
} from 'antd';
import { PlusOutlined, DeleteOutlined, MinusCircleOutlined } from '@ant-design/icons';
import {
  CreateFiatRequest,
  CreateSoupRequest,
  CreateExchangeRequest,
  CreateFilterRequest,
} from '../../types/api.types';
import { useDataContext } from '../../contexts/DataContext';
import ApiService from '../../services/api';
import { MESSAGES, FILTER_LABELS,  } from '../../utils/constants';

const { Title } = Typography;
const { Step } = Steps;

interface AddFiatModalProps {
  visible: boolean;
  onCancel: () => void;
  onSuccess: () => void;
}

interface SoupFormData extends Omit<CreateSoupRequest, 'exchanges'> {
  exchanges: ExchangeFormData[];
}

interface ExchangeFormData extends Omit<CreateExchangeRequest, 'filter_buy' | 'filter_sell'> {
  useDifferentSellSettings: boolean;
  filter_buy: FilterFormData;
  filter_sell: FilterFormData;
}

interface FilterFormData extends CreateFilterRequest {}

const AddFiatModal: React.FC<AddFiatModalProps> = ({
  visible,
  onCancel,
  onSuccess,
}) => {
  const [form] = Form.useForm();
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [selectedFiat, setSelectedFiat] = useState<string>('');
  const [soups, setSoups] = useState<SoupFormData[]>([]);
  const [availableExchanges, setAvailableExchanges] = useState<Record<number, string[]>>({});
  const [availableBanks, setAvailableBanks] = useState<Record<string, string[]>>({});
  const [loadingBanks, setLoadingBanks] = useState<Record<string, boolean>>({});
  const [useDifferentSellSettings, setUseDifferentSellSettings] = useState(false);

  const { allFiats, usedFiats, refreshData } = useDataContext();

  const availableFiats = (Array.isArray(allFiats) && Array.isArray(usedFiats))
    ? allFiats.filter(fiat => !usedFiats.includes(fiat))
    : [];

  const resetForm = useCallback(() => {
    setCurrentStep(0);
    setSelectedFiat('');
    setSoups([]);
    setAvailableExchanges({});
    setAvailableBanks({});
    setLoadingBanks({});
    setUseDifferentSellSettings(false);
    form.resetFields();
  }, [form]);

  useEffect(() => {
    if (!visible) {
      resetForm();
    }
  }, [visible, resetForm]);

  // Автоматическая загрузка бирж при переходе к шагу настройки бирж
  useEffect(() => {
    if (currentStep === 2 && selectedFiat && soups.length > 0) {
      soups.forEach((_, soupIndex) => {
        if (!availableExchanges[soupIndex]) {
          loadExchanges(soupIndex);
        }
      });
    }
  }, [currentStep, selectedFiat, soups.length]);

  const handleFiatSelect = async (fiatName: string) => {
    setSelectedFiat(fiatName);
    const defaultSoup: SoupFormData = {
      name: '',
      average: 0,
      exchanges: [],
    };
    setSoups([defaultSoup]);
  };

  const addSoup = () => {
    const newSoup: SoupFormData = {
      name: '',
      average: 0,
      exchanges: [],
    };
    setSoups([...soups, newSoup]);
  };

  const removeSoup = (index: number) => {
    if (soups.length > 1) {
      setSoups(soups.filter((_, i) => i !== index));
    }
  };

  const updateSoup = (index: number, field: keyof SoupFormData, value: any) => {
    const updatedSoups = [...soups];
    updatedSoups[index] = { ...updatedSoups[index], [field]: value };
    setSoups(updatedSoups);
  };

  const defaultFilter: FilterFormData = {
    month_order: 0,
    month_finish_rate: 0,
    max_low_single_trans_amount: 0,
    min_high_single_trans_amount: 0,
    average_size: 0,
  };

  const addExchange = (soupIndex: number) => {
    const newExchange: ExchangeFormData = {
      exchange_name: '',
      banks: [],
      useDifferentSellSettings: false,
      filter_buy: { ...defaultFilter },
      filter_sell: { ...defaultFilter },
    };
    updateSoup(soupIndex, 'exchanges', [...soups[soupIndex].exchanges, newExchange]);
  };

  const removeExchange = (soupIndex: number, exchangeIndex: number) => {
    const exchanges = soups[soupIndex].exchanges.filter((_, i) => i !== exchangeIndex);
    updateSoup(soupIndex, 'exchanges', exchanges);
  };

  const updateExchange = (
    soupIndex: number,
    exchangeIndex: number,
    field: keyof ExchangeFormData,
    value: any
  ) => {
    const updatedSoups = [...soups];
    updatedSoups[soupIndex].exchanges[exchangeIndex] = {
      ...updatedSoups[soupIndex].exchanges[exchangeIndex],
      [field]: value,
    };
    setSoups(updatedSoups);
  };

  const loadExchanges = async (soupIndex: number) => {
    if (!selectedFiat) return;
    try {
      const exchanges = await ApiService.getExchanges(selectedFiat);
      setAvailableExchanges(prev => ({ ...prev, [soupIndex]: exchanges }));
    } catch (error) {
      console.error('Ошибка загрузки бирж:', error);
      message.error('Не удалось загрузить список бирж');
    }
  };

  const loadBanks = async (exchangeName: string) => {
    if (!selectedFiat || !exchangeName) return;
    const key = `${selectedFiat}-${exchangeName}`;
    setLoadingBanks(prev => ({ ...prev, [key]: true }));
    try {
      const banks = await ApiService.getBanks(selectedFiat, exchangeName);
      setAvailableBanks(prev => ({ ...prev, [key]: banks }));
    } catch (error) {
      console.error('Ошибка загрузки банков:', error);
      message.error('Не удалось загрузить список банков');
    } finally {
      setLoadingBanks(prev => ({ ...prev, [key]: false }));
    }
  };

  const handleExchangeSelect = (soupIndex: number, exchangeIndex: number, exchangeName: string) => {
    updateExchange(soupIndex, exchangeIndex, 'exchange_name', exchangeName);
    loadBanks(exchangeName);
  };

  const updateExchangeFilter = (
    soupIndex: number,
    exchangeIndex: number,
    filterType: 'buy' | 'sell',
    field: keyof FilterFormData,
    value: any
  ) => {
    const updatedSoups = [...soups];
    const exchange = updatedSoups[soupIndex].exchanges[exchangeIndex];
    const filterKey = `filter_${filterType}` as const;

    exchange[filterKey] = {
      ...exchange[filterKey],
      [field]: value,
    };

    if (!useDifferentSellSettings && filterType === 'buy') {
      exchange.filter_sell = {
        ...exchange.filter_sell,
        [field]: value,
      };
    }

    setSoups(updatedSoups);
  };

  const handleUseDifferentSellSettingsChange = (checked: boolean) => {
    setUseDifferentSellSettings(checked);
    if (!checked) {
      const updatedSoups = soups.map(soup => ({
        ...soup,
        exchanges: soup.exchanges.map(exchange => ({
          ...exchange,
          filter_sell: { ...exchange.filter_buy }
        }))
      }));
      setSoups(updatedSoups);
    }
  };

  const nextStep = () => {
    if (currentStep === 0 && !selectedFiat) {
      message.error('Выберите валюту');
      return;
    }
    if (currentStep === 1) {
      const hasValidSoups = soups.every(soup => soup.name && soup.average > 0);
      if (!hasValidSoups) {
        message.error('Заполните все поля супов');
        return;
      }
    }
    if (currentStep === 2) {
      // Проверяем, что у каждого супа есть хотя бы одна биржа
      const soupsWithoutExchanges = soups.filter(soup => soup.exchanges.length === 0);
      if (soupsWithoutExchanges.length > 0) {
        message.error('Каждый суп должен содержать хотя бы одну биржу');
        return;
      }

      // Проверяем, что у каждой биржи выбрано название
      const exchangesWithoutName = soups.some(soup =>
        soup.exchanges.some(exchange => !exchange.exchange_name)
      );
      if (exchangesWithoutName) {
        message.error('Выберите название биржи для всех добавленных бирж');
        return;
      }

      // Проверяем, что у каждой биржи выбран хотя бы один банк
      const exchangesWithoutBanks = soups.some(soup =>
        soup.exchanges.some(exchange => exchange.banks.length === 0)
      );
      if (exchangesWithoutBanks) {
        message.error('Для каждой биржи должен быть выбран хотя бы один банк');
        return;
      }
    }
    setCurrentStep(currentStep + 1);
  };

  const prevStep = () => {
    setCurrentStep(currentStep - 1);
  };

  const validateFilters = (): boolean => {
    const validate = (filter: FilterFormData) =>
        filter.month_order > 0 &&
        filter.month_finish_rate >= 0 &&
        filter.max_low_single_trans_amount > 0 &&
        filter.min_high_single_trans_amount > 0 &&
        filter.average_size > 0;

    return soups.every(soup =>
      soup.exchanges.every(exchange => {
        const isBuyValid = validate(exchange.filter_buy);
        const isSellValid = useDifferentSellSettings ? validate(exchange.filter_sell) : true;
        return isBuyValid && isSellValid;
      })
    );
  };

  const handleSubmit = async () => {
    if (!validateFilters()) {
      message.error('Заполните все поля фильтров');
      return;
    }

    setLoading(true);
    try {
      const createData: CreateFiatRequest = {
        name: selectedFiat,
        exchange_soups: soups.map(soup => ({
          name: soup.name,
          average: soup.average,
          exchanges: soup.exchanges.map(exchange => ({
            exchange_name: exchange.exchange_name,
            banks: exchange.banks,
            filter_buy: exchange.filter_buy,
            filter_sell: useDifferentSellSettings ? exchange.filter_sell : exchange.filter_buy,
          })),
        })),
      };

      await ApiService.createFiat(createData);
      message.success(MESSAGES.SUCCESS.FIAT_CREATED);
      await refreshData();
      onSuccess();
    } catch (error) {
      console.error('Ошибка создания валюты:', error);
      message.error(MESSAGES.ERROR.GENERIC);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    resetForm();
    onCancel();
  };

  const steps = [
    { title: 'Валюта', content: 'Выберите валюту для добавления' },
    { title: 'Супы', content: 'Настройте супы' },
    { title: 'Биржи', content: 'Выберите биржи и банки' },
    { title: 'Фильтры', content: 'Настройте фильтры для торговли' },
  ];

  const getAvailableExchangesForSoup = (soupIndex: number) => {
    const allExchanges = availableExchanges[soupIndex] || [];
    const usedExchanges = soups[soupIndex].exchanges.map(ex => ex.exchange_name).filter(Boolean);
    return allExchanges.filter(exchange => !usedExchanges.includes(exchange));
  };

  const canAddMoreExchanges = (soupIndex: number) => {
    return getAvailableExchangesForSoup(soupIndex).length > 0;
  };

  return (
    <Modal
      title="Добавить новую валюту"
      open={visible}
      onCancel={handleCancel}
      footer={null}
      width="95%"
      style={{ maxWidth: 1000, top: 20 }}
      destroyOnClose
    >
      <Steps current={currentStep} style={{ marginBottom: 24 }}>
        {steps.map(item => (
          <Step key={item.title} title={item.title} description={item.content} />
        ))}
      </Steps>

      {/* Шаг 1: Выбор валюты */}
      {currentStep === 0 && (
        <div>
          <Title level={4}>Выберите валюту</Title>
          <Select
            placeholder="Выберите валюту для добавления"
            style={{ width: '100%' }}
            value={selectedFiat}
            onChange={handleFiatSelect}
            options={availableFiats.map(fiat => ({ label: fiat, value: fiat }))}
          />
        </div>
      )}

      {/* Шаг 2: Настройка супов */}
      {currentStep === 1 && (
        <div>
          <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
            <Col><Title level={4}>Настройка супов для {selectedFiat}</Title></Col>
            <Col><Button type="dashed" icon={<PlusOutlined />} onClick={addSoup}>Добавить суп</Button></Col>
          </Row>
          {soups.map((soup, index) => (
            <Card
              key={index}
              title={`Суп ${index + 1}`}
              extra={soups.length > 1 && <Button type="text" danger icon={<DeleteOutlined />} onClick={() => removeSoup(index)} />}
              style={{ marginBottom: 16 }}
            >
              <Row gutter={16}>
                <Col xs={24} sm={12}>
                  <Form.Item label="Название супа" required>
                    <Input placeholder="Введите название супа" value={soup.name} onChange={(e) => updateSoup(index, 'name', e.target.value)} />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item label="Среднее значение" required>
                    <InputNumber
                      placeholder="Введите среднее значение"
                      style={{ width: '100%' }}
                      min={0}
                      step={1}
                      precision={0}
                      value={soup.average}
                      onChange={(value) => updateSoup(index, 'average', value || 0)}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Card>
          ))}
        </div>
      )}

      {/* Шаг 3: Настройка бирж */}
      {currentStep === 2 && (
        <div>
          <Title level={4}>Настройка бирж</Title>
          {soups.map((soup, soupIndex) => (
            <Card key={soupIndex} title={`Суп: ${soup.name}`} style={{ marginBottom: 16 }}>
              {soup.exchanges.map((exchange, exchangeIndex) => {
                const banksKey = `${selectedFiat}-${exchange.exchange_name}`;
                const isLoadingBanks = loadingBanks[banksKey];
                const banks = availableBanks[banksKey] || [];
                return (
                  <Card
                    key={exchangeIndex}
                    size="small"
                    title={`Биржа ${exchangeIndex + 1}`}
                    extra={<Button type="text" danger icon={<MinusCircleOutlined />} onClick={() => removeExchange(soupIndex, exchangeIndex)} />}
                    style={{ marginBottom: 12 }}
                  >
                    <Row gutter={16}>
                      <Col xs={24} sm={12}>
                        <Form.Item label="Выберите биржу" required>
                          <Select
                            placeholder="Выберите биржу"
                            value={exchange.exchange_name || undefined}
                            onChange={(value) => handleExchangeSelect(soupIndex, exchangeIndex, value)}
                            options={getAvailableExchangesForSoup(soupIndex).concat(
                              exchange.exchange_name ? [exchange.exchange_name] : []
                            ).map(ex => ({ label: ex, value: ex }))}
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12}>
                        <Form.Item
                          label="Выберите банки"
                          required
                          validateStatus={exchange.banks.length === 0 && exchange.exchange_name ? 'error' : ''}
                          help={exchange.banks.length === 0 && exchange.exchange_name ? 'Необходимо выбрать хотя бы один банк' : ''}
                        >
                          <Spin spinning={isLoadingBanks}>
                            <Checkbox.Group
                              value={exchange.banks}
                              onChange={(values) => updateExchange(soupIndex, exchangeIndex, 'banks', values)}
                              disabled={!exchange.exchange_name || isLoadingBanks}
                            >
                              <div style={{
                                maxHeight: '200px',
                                overflowY: 'auto',
                                border: `1px solid ${exchange.banks.length === 0 && exchange.exchange_name ? '#ff4d4f' : '#d9d9d9'}`,
                                borderRadius: '8px',
                                padding: '12px',
                                backgroundColor: '#fafafa',
                                boxShadow: 'inset 0 1px 3px rgba(0, 0, 0, 0.1)'
                              }}>
                                {banks.length === 0 && !isLoadingBanks && exchange.exchange_name ? (
                                  <div style={{
                                    textAlign: 'center',
                                    color: '#999',
                                    padding: '20px'
                                  }}>
                                    Банки не найдены для выбранной биржи
                                  </div>
                                ) : (
                                  <div style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: '8px'
                                  }}>
                                    {banks.sort((a, b) => a.localeCompare(b, 'ru', { sensitivity: 'base' })).map((bank) => (
                                      <Checkbox
                                        key={bank}
                                        value={bank}
                                        style={{
                                          padding: '4px 8px',
                                          borderRadius: '4px',
                                          backgroundColor: 'white',
                                          border: '1px solid #e8e8e8',
                                          transition: 'all 0.2s ease'
                                        }}
                                      >
                                        {bank}
                                      </Checkbox>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </Checkbox.Group>
                          </Spin>
                        </Form.Item>
                      </Col>
                    </Row>
                  </Card>
                );
              })}
              <Button
                type="dashed"
                icon={<PlusOutlined />}
                onClick={() => {
                  addExchange(soupIndex);
                  if (!availableExchanges[soupIndex]) {
                    loadExchanges(soupIndex);
                  }
                }}
                style={{ width: '100%' }}
                disabled={availableExchanges[soupIndex] && !canAddMoreExchanges(soupIndex)}
                loading={!availableExchanges[soupIndex]}
              >
                {!availableExchanges[soupIndex] ? 'Загрузка бирж...' : 'Добавить биржу к супу'}
              </Button>
            </Card>
          ))}
        </div>
      )}

      {/* Шаг 4: Настройка фильтров */}
      {currentStep === 3 && (
        <div>
          <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
            <Col><Title level={4}>Настройка фильтров</Title></Col>
            <Col>
              <Checkbox checked={useDifferentSellSettings} onChange={(e) => handleUseDifferentSellSettingsChange(e.target.checked)}>
                Использовать разные настройки для продажи
              </Checkbox>
            </Col>
          </Row>
          {soups.map((soupItem, soupIndex) => (
            <Card key={soupIndex} title={`Суп: ${soupItem.name}`} style={{ marginBottom: 16 }}>
              {soupItem.exchanges.map((exchange, exchangeIndex) => (
                <Card key={exchangeIndex} size="small" title={`Биржа: ${exchange.exchange_name}`} style={{ marginBottom: 12 }}>
                  <Card size="small" title="Фильтр покупки" style={{ marginBottom: 16 }}>
                    <Row gutter={[16, 16]}>
                      <Col xs={24} sm={12}>
                        <Form.Item label={FILTER_LABELS.month_order} style={{ marginBottom: 8 }}>
                          <InputNumber
                            value={exchange.filter_buy.month_order}
                            onChange={(val) => updateExchangeFilter(soupIndex, exchangeIndex, 'buy', 'month_order', val || 0)}
                            style={{ width: '100%' }}
                            min={0}
                            step={1}
                            placeholder="Введите значение"
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12}>
                        <Form.Item label={FILTER_LABELS.month_finish_rate} style={{ marginBottom: 8 }}>
                          <InputNumber
                            value={exchange.filter_buy.month_finish_rate}
                            onChange={(val) => updateExchangeFilter(soupIndex, exchangeIndex, 'buy', 'month_finish_rate', val || 0)}
                            style={{ width: '100%' }}
                            min={0}
                            max={1}
                            step={0.01}
                            placeholder="0.00 - 1.00"
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12}>
                        <Form.Item label={FILTER_LABELS.max_low_single_trans_amount} style={{ marginBottom: 8 }}>
                          <InputNumber
                            value={exchange.filter_buy.max_low_single_trans_amount}
                            onChange={(val) => updateExchangeFilter(soupIndex, exchangeIndex, 'buy', 'max_low_single_trans_amount', val || 0)}
                            style={{ width: '100%' }}
                            min={0}
                            placeholder="Введите сумму"
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12}>
                        <Form.Item label={FILTER_LABELS.min_high_single_trans_amount} style={{ marginBottom: 8 }}>
                          <InputNumber
                            value={exchange.filter_buy.min_high_single_trans_amount}
                            onChange={(val) => updateExchangeFilter(soupIndex, exchangeIndex, 'buy', 'min_high_single_trans_amount', val || 0)}
                            style={{ width: '100%' }}
                            min={0}
                            placeholder="Введите сумму"
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12}>
                        <Form.Item label={FILTER_LABELS.average_size} style={{ marginBottom: 8 }}>
                          <InputNumber
                            value={exchange.filter_buy.average_size}
                            onChange={(val) => updateExchangeFilter(soupIndex, exchangeIndex, 'buy', 'average_size', val || 0)}
                            style={{ width: '100%' }}
                            min={0}
                            step={1}
                            precision={0}
                            placeholder="Введите размер"
                          />
                        </Form.Item>
                      </Col>
                    </Row>
                  </Card>
                  {useDifferentSellSettings && (
                    <Card size="small" title="Фильтр продажи" style={{ marginBottom: 16 }}>
                      <Row gutter={[16, 16]}>
                        <Col xs={24} sm={12}>
                          <Form.Item label={FILTER_LABELS.month_order} style={{ marginBottom: 8 }}>
                            <InputNumber
                              value={exchange.filter_sell.month_order}
                              onChange={(val) => updateExchangeFilter(soupIndex, exchangeIndex, 'sell', 'month_order', val || 0)}
                              style={{ width: '100%' }}
                              min={0}
                              step={1}
                              placeholder="Введите значение"
                            />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={12}>
                          <Form.Item label={FILTER_LABELS.month_finish_rate} style={{ marginBottom: 8 }}>
                            <InputNumber
                              value={exchange.filter_sell.month_finish_rate}
                              onChange={(val) => updateExchangeFilter(soupIndex, exchangeIndex, 'sell', 'month_finish_rate', val || 0)}
                              style={{ width: '100%' }}
                              min={0}
                              max={1}
                              step={0.01}
                              placeholder="0.00 - 1.00"
                            />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={12}>
                          <Form.Item label={FILTER_LABELS.max_low_single_trans_amount} style={{ marginBottom: 8 }}>
                            <InputNumber
                              value={exchange.filter_sell.max_low_single_trans_amount}
                              onChange={(val) => updateExchangeFilter(soupIndex, exchangeIndex, 'sell', 'max_low_single_trans_amount', val || 0)}
                              style={{ width: '100%' }}
                              min={0}
                              placeholder="Введите сумму"
                            />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={12}>
                          <Form.Item label={FILTER_LABELS.min_high_single_trans_amount} style={{ marginBottom: 8 }}>
                            <InputNumber
                              value={exchange.filter_sell.min_high_single_trans_amount}
                              onChange={(val) => updateExchangeFilter(soupIndex, exchangeIndex, 'sell', 'min_high_single_trans_amount', val || 0)}
                              style={{ width: '100%' }}
                              min={0}
                              placeholder="Введите сумму"
                            />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={12}>
                          <Form.Item label={FILTER_LABELS.average_size} style={{ marginBottom: 8 }}>
                            <InputNumber
                              value={exchange.filter_sell.average_size}
                              onChange={(val) => updateExchangeFilter(soupIndex, exchangeIndex, 'sell', 'average_size', val || 0)}
                              style={{ width: '100%' }}
                              min={0}
                              step={1}
                              precision={0}
                              placeholder="Введите размер"
                            />
                          </Form.Item>
                        </Col>
                      </Row>
                    </Card>
                  )}
                </Card>
              ))}
            </Card>
          ))}
        </div>
      )}

      {/* Навигация */}
      <div style={{ marginTop: 24, textAlign: 'right' }}>
        <Space>
          <Button onClick={handleCancel}>Отмена</Button>
          {currentStep > 0 && <Button onClick={prevStep}>Назад</Button>}
          {currentStep < steps.length - 1 && <Button type="primary" onClick={nextStep}>Далее</Button>}
          {currentStep === steps.length - 1 && <Button type="primary" loading={loading} onClick={handleSubmit}>Создать валюту</Button>}
        </Space>
      </div>
    </Modal>
  );
};

export default AddFiatModal;
